import { gqlRequest } from '@mdvip/shared/lib/graphql-client';
import {
  CancelUpcomingPaymentDocument,
  type CancelUpcomingPaymentFailureCode,
  MyUpcomingPaymentsDocument,
  type MyUpcomingPaymentsQuery,
} from '@/graphql/generated/graphql';

export type UpcomingPayment = MyUpcomingPaymentsQuery['myUpcomingPayments'][number];

/**
 * Thrown when cancelUpcomingPayment returns its Failure variant. `NOT_FOUND` is currently the only
 * code, and it deliberately covers an unknown, someone else's, or an already-cancelled payment so
 * the response leaks no enumeration oracle. Typed to the generated enum so TypeScript flags the
 * unhandled branches if the backend adds codes.
 */
export class CancelUpcomingPaymentError extends Error {
  constructor(public readonly code: CancelUpcomingPaymentFailureCode) {
    super(code);
    this.name = 'CancelUpcomingPaymentError';
  }
}

export async function requestUpcomingPayments(): Promise<UpcomingPayment[]> {
  const data = await gqlRequest(MyUpcomingPaymentsDocument);
  return data.myUpcomingPayments;
}

/** Cancels one scheduled payment. Resolves with the cancelled id so the caller can prune its cache. */
export async function requestCancelUpcomingPayment(id: string): Promise<string> {
  const data = await gqlRequest(CancelUpcomingPaymentDocument, { input: { id } });
  const result = data.cancelUpcomingPayment;
  if (result.__typename === 'CancelUpcomingPaymentFailure') {
    throw new CancelUpcomingPaymentError(result.code);
  }
  return result.id;
}
